import { useCallback } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useSubscription } from "urql"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { MessageQuery, OutboundDeliveryUpdatedSubscription } from "@/lib/graphql/outbound"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"

type DeliveryItem = NonNullable<ResultOf<typeof MessageQuery>["message"]>["outboundDeliveries"][number]

const deliveryColumns: Column<DeliveryItem>[] = [
  {
    header: "Endpoint",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-60 truncate block">
        {row.endpoint.url}
      </code>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function MessageDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isConfigured } = useAuth()
  const [result, reexecute] = useQuery({ query: MessageQuery, variables: { id: id! } })

  const handleDeliveryUpdated = useCallback(
    (
      _prev: ResultOf<typeof OutboundDeliveryUpdatedSubscription> | undefined,
      response: ResultOf<typeof OutboundDeliveryUpdatedSubscription>,
    ) => {
      if (response?.outboundDeliveryUpdated) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription(
    { query: OutboundDeliveryUpdatedSubscription, variables: { messageId: id }, pause: !isConfigured },
    handleDeliveryUpdated,
  )

  const msg = result.data?.message

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!msg) return <EmptyState title="Message not found" />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{msg.eventType}</h1>
          <StatusBadge status={msg.status} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Message</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Event Type</FieldLabel>
                <code className="font-mono text-xs">{msg.eventType}</code>
              </div>
              <div>
                <FieldLabel className="mb-1">Application</FieldLabel>
                <Link to={`/applications/${msg.application.id}`} className="text-sm hover:underline">
                  {msg.application.name}
                </Link>
              </div>
              <div>
                <FieldLabel className="mb-1">Idempotency Key</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs truncate">{msg.idempotencyKey}</code>
                  <CopyButton text={msg.idempotencyKey} />
                </div>
              </div>
            </div>
            {msg.payload && (
              <div>
                <FieldLabel className="mb-2">Payload</FieldLabel>
                <pre className="text-xs font-mono bg-muted rounded-lg p-3 overflow-x-auto max-h-60">
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(atob(msg.payload)), null, 2)
                    } catch {
                      return atob(msg.payload)
                    }
                  })()}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{msg.id}</code>
                <CopyButton text={msg.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={msg.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Deliveries</h2>
        <DataTable
          columns={deliveryColumns}
          data={msg.outboundDeliveries}
          keyFn={(row) => row.id}
          emptyTitle="No deliveries"
          onRowClick={(row) => navigate(`/outbound-deliveries/${row.id}`)}
        />
      </div>
    </div>
  )
}
